import React, { useContext, useState, useEffect } from "react";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import ListGroup from "react-bootstrap/ListGroup";
import Row from "react-bootstrap/esm/Row";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router-dom";
import { Store } from "../Store.js";
import MessageBox from "../components/MessageBox.js";
import Card from "react-bootstrap/Card";
import Form from "react-bootstrap/Form";
import axios from "axios";
import { toast } from "react-toastify";
import { getError } from "../utils";
import VoiceRecorder from "../components/VoiceRecorder";

export default function CartScreen() {
  const navigate = useNavigate();
  const { state, dispatch: ctxDispatch } = useContext(Store);
  const { cart } = state;
  const { cartItems } = cart;
  const [loading, setLoading] = useState(false);
  const [tableInfo, setTableInfo] = useState(null);
  const [customerName, setCustomerName] = useState("");
  const [tableNumber, setTableNumber] = useState("");
  const [restaurantId, setRestaurantId] = useState(null);
  const [specialInstructions, setSpecialInstructions] = useState("");
  const [showRecorder, setShowRecorder] = useState(false);
  const [instructionsAudio, setInstructionsAudio] = useState(null);
  const [instructionsAudioUrl, setInstructionsAudioUrl] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    // Try to get table info from storage
    const savedTable = localStorage.getItem("tableInfo");
    if (savedTable) {
      const parsedTable = JSON.parse(savedTable);
      setTableInfo(parsedTable);
      setTableNumber(
        parsedTable.type === "room"
          ? parsedTable.roomNumber
          : parsedTable.tableNumber
      );
    }
    const savedBusinessId = localStorage.getItem("currentBusinessId");
    if (savedBusinessId) {
      setRestaurantId(savedBusinessId);
    }
  }, []);

  const updateCartHandler = async (item, quantity) => {
    // Check stock if needed, or just allow for food items
    ctxDispatch({
      type: "CART_ADD_ITEM",
      payload: { ...item, quantity },
    });
  };

  const removeItemHandler = (item) => {
    ctxDispatch({ type: "CART_REMOVE_ITEM", payload: item });
  };

  const uploadFileHandler = async (file) => {
    if (!file) return null;
    try {
      setIsUploading(true);
      const bodyFormData = new FormData();

      // For audio blobs, provide explicit filename
      if (file instanceof Blob && !(file instanceof File)) {
        const extension = file.type.includes("webm")
          ? "webm"
          : file.type.includes("ogg")
          ? "ogg"
          : file.type.includes("wav")
          ? "wav"
          : "bin";
        bodyFormData.append(
          "file",
          file,
          `instructions_${Date.now()}.${extension}`
        );
      } else {
        bodyFormData.append("file", file);
      }

      const { data } = await axios.post("/api/upload", bodyFormData);
      setIsUploading(false);
      return data.secure_url;
    } catch (err) {
      console.error("Upload error:", err);
      toast.error(getError(err));
      setIsUploading(false);
      return null;
    }
  };

  const placeOrderHandler = async () => {
    if (!tableNumber) {
      toast.error(
        `Please enter a ${tableInfo?.type === "room" ? "room" : "table"} number`
      );
      return;
    }

    try {
      setLoading(true);

      // Upload voice instructions if recorded
      let audioUrl = "";
      if (instructionsAudio) {
        audioUrl = await uploadFileHandler(instructionsAudio);
      }

      const { data } = await axios.post("/api/orders", {
        orderItems: cartItems,
        itemsPrice: cartItems.reduce((a, c) => a + c.price * c.quantity, 0),
        totalPrice: cartItems.reduce((a, c) => a + c.price * c.quantity, 0),
        tableNumber: tableNumber,
        tableId: tableInfo ? tableInfo._id : null,
        locationType: tableInfo ? tableInfo.type : "table",
        customerName: customerName || "Guest",
        paymentMethod: "Cash",
        restaurantId: restaurantId,
        specialInstructions: specialInstructions,
        specialInstructionsAudio: audioUrl,
      });

      ctxDispatch({ type: "CART_CLEAR" });
      localStorage.removeItem("cartItems");
      toast.success("Order placed successfully!");

      // Navigate back to menu with active order view
      if (tableInfo && tableInfo._id) {
        navigate(`/menu?table=${tableInfo._id}&business=${restaurantId || ""}`);
      } else {
        navigate(`/order/${data.order._id}`);
      }

      setLoading(false);
    } catch (err) {
      setLoading(false);
      toast.error(getError(err));
    }
  };

  return (
    <div>
      <Helmet>
        <title>Your Order</title>
      </Helmet>
      <h1 className="mb-4">Your Order</h1>

      <Row>
        <Col md={8}>
          {cartItems.length === 0 ? (
            <MessageBox>
              Your cart is empty. <Link to="/menu">Go to Menu</Link>
            </MessageBox>
          ) : (
            <ListGroup variant="flush">
              {cartItems.map((item) => (
                <ListGroup.Item
                  key={item._id || item.slug}
                  className="mb-3 shadow-sm rounded border-0"
                >
                  <Row className="align-items-center">
                    <Col md={4}>
                      <img
                        src={item.image}
                        alt={item.name}
                        className="img-fluid rounded img-thumbnail"
                        style={{ height: "80px", objectFit: "cover" }}
                      ></img>{" "}
                      <span className="ms-3 fw-bold">{item.name}</span>
                    </Col>
                    <Col md={3} className="my-2 my-md-0">
                      <div className="d-flex align-items-center">
                        <Button
                          variant="light"
                          className="rounded-circle"
                          onClick={() =>
                            updateCartHandler(item, item.quantity - 1)
                          }
                          disabled={item.quantity === 1}
                        >
                          <i className="fas fa-minus-circle"></i>
                        </Button>
                        <span className="mx-3 fw-bold">{item.quantity}</span>
                        <Button
                          variant="light"
                          className="rounded-circle"
                          onClick={() =>
                            updateCartHandler(item, item.quantity + 1)
                          }
                        >
                          <i className="fas fa-plus-circle"></i>
                        </Button>
                      </div>
                    </Col>
                    <Col md={3} className="fw-bold text-primary">
                      {item.price} RWF
                    </Col>
                    <Col md={2} className="text-end">
                      <Button
                        onClick={() => removeItemHandler(item)}
                        variant="light"
                        className="text-danger"
                      >
                        <i className="fas fa-trash"></i>
                      </Button>
                    </Col>
                  </Row>
                  {item.specialInstructions && (
                    <div className="mt-2 text-muted small fst-italic ps-2 border-start border-3 border-warning">
                      Note: {item.specialInstructions}
                    </div>
                  )}
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </Col>
        <Col md={4}>
          <Card className="shadow-sm border-0">
            <Card.Body>
              <ListGroup variant="flush">
                <ListGroup.Item className="border-0 px-0">
                  <h3 className="d-flex justify-content-between">
                    <span>
                      Subtotal ({cartItems.reduce((a, c) => a + c.quantity, 0)}{" "}
                      items)
                    </span>
                    <span className="fw-bold text-primary">
                      {cartItems.reduce((a, c) => a + c.price * c.quantity, 0)}{" "}
                      RWF
                    </span>
                  </h3>
                </ListGroup.Item>

                <ListGroup.Item className="px-0 py-3">
                  <Form.Group className="mb-3">
                    <Form.Label>
                      {tableInfo?.type === "room"
                        ? "Room Number"
                        : "Table Number"}
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={tableNumber}
                      onChange={(e) => setTableNumber(e.target.value)}
                      placeholder="e.g. 5"
                      required
                      className="form-control-lg"
                    />
                    <Form.Text className="text-muted">
                      {tableInfo
                        ? "Detected from QR Code"
                        : `Please enter your ${
                            tableInfo?.type === "room" ? "room" : "table"
                          } number`}
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Your Name (Optional)</Form.Label>
                    <Form.Control
                      type="text"
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      placeholder="Enter your name"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Special Instructions (Optional)</Form.Label>
                    {!showRecorder ? (
                      <Form.Control
                        as="textarea"
                        rows={2}
                        value={specialInstructions}
                        onChange={(e) => setSpecialInstructions(e.target.value)}
                        placeholder="Any special requests? (e.g., no onions, extra spicy...)"
                        className="mb-2"
                      />
                    ) : (
                      <VoiceRecorder
                        onRecordingComplete={(blob, url) => {
                          setInstructionsAudio(blob);
                          setInstructionsAudioUrl(url);
                          setShowRecorder(false);
                        }}
                        onCancel={() => {
                          setShowRecorder(false);
                          setInstructionsAudio(null);
                          setInstructionsAudioUrl("");
                        }}
                      />
                    )}

                    {instructionsAudioUrl && (
                      <div className="mt-2 p-2 bg-light rounded border">
                        <div className="d-flex align-items-center gap-2">
                          <audio
                            src={instructionsAudioUrl}
                            controls
                            style={{ width: "100%", height: "40px" }}
                          />
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => {
                              setInstructionsAudio(null);
                              setInstructionsAudioUrl("");
                            }}
                            title="Delete recording"
                          >
                            <i className="fas fa-trash"></i>
                          </Button>
                        </div>
                      </div>
                    )}

                    {!showRecorder && !instructionsAudioUrl && (
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setShowRecorder(true)}
                        className="mt-2"
                      >
                        <i className="fas fa-microphone me-2"></i>
                        Record Voice Instructions
                      </Button>
                    )}
                  </Form.Group>
                </ListGroup.Item>

                <div className="d-grid mt-3">
                  <Button
                    type="button"
                    variant="primary"
                    size="lg"
                    onClick={placeOrderHandler}
                    disabled={cartItems.length === 0 || loading}
                  >
                    {loading ? "Placing Order..." : "Place Order"}
                  </Button>
                </div>
              </ListGroup>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
